import {Cache, cacheable} from 'shared/utils/cache';
import type {PermissionResponse, PermissionsDataService} from './permissions.data-service';
import type {Permission} from './permissions.builder';

const CACHE_NAME = 'SECURITY_MANAGER';
const CACHE_STORAGE = window.sessionStorage;

/**
 * @ngdoc service
 * @name permissionsDataService
 * @module portaro.data-services
 *
 * @requires service:securityManagerDataService
 * @requires cacheable
 *
 * @description
 * Cached data service for determining user rights.
 * Cache is located in Session Storage under name `'SECURITY_MANAGER'`.
 */
export default class CachedPermissionsDataService implements PermissionsDataService {
    public static serviceName = 'cachedPermissionsDataService';

    /**
     * Clear specified cache
     */
    public static clearCache() {
        Cache.getInstance(CACHE_STORAGE, CACHE_NAME).clear();
    }

    /*@ngInject*/
    constructor(private permissionsDataService: PermissionsDataService) {
    }

    /**
     * @ngdoc method
     * @name permissionsDataService#canDoAction
     *
     * @param {string} action Action name
     * @param {object=} subject Additional info
     *
     * @returns {Promise<boolean>} Result
     *
     * @description
     * Ask server, if current user can do some action described by action name and, if needed, by additional info.
     */
    @cacheable(CACHE_STORAGE, CACHE_NAME)
    public async canDoAction(action: string, subject: any): Promise<boolean> {
        return this.permissionsDataService.canDoAction(action, subject);
    }

    /**
     * @ngdoc method
     * @name permissionsDataService#canDoActions
     *
     * @param {Array.<{action: string, subject: object}>} permissions
     *
     * @returns {Promise.<object>} Result. e.g. `{action1: true, action2: false}`
     *
     * @description
     * Ask server, if current user can do specified actions described by array of action name and, if needed, by additional info in subject.
     *
     * _**Example**_
     *
     * ```js
     *      const permissions = [
     *          {
     *              action: 'action1',
     *              subject: null
     *          },
     *          {
     *              action: 'action2',
     *              subject: {
     *                  department: 1
     *              }
     *          }
     *      ];
     *      permissionsDataService.canDoActions(permissions);
     * ```
     */
    @cacheable(CACHE_STORAGE, CACHE_NAME)
    public async canDoActions<E extends PermissionResponse>(permissions: Permission[]): Promise<E> {
        return this.permissionsDataService.canDoActions(permissions);
    }
}
